import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export function useOnboardingCheck() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // Don't check if auth is still loading or no user
      if (authLoading || !user) {
        setLoading(false);
        return;
      }

      // Don't check if already on auth or onboarding pages
      const currentPath = location.pathname;
      if (currentPath === '/auth' || currentPath === '/onboarding') {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('user_id', user.id)
          .single();

        if (error) {
          console.error('Error checking onboarding status:', error);
          setLoading(false);
          return;
        }

        const completed = data?.onboarding_completed || false;
        setOnboardingCompleted(completed);

        // Redirect to onboarding if not completed
        if (!completed && currentPath !== '/onboarding') {
          navigate('/onboarding', { replace: true });
        }
      } catch (error) {
        console.error('Error in onboarding check:', error);
      } finally {
        setLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [user, authLoading, navigate, location.pathname]);

  return {
    onboardingCompleted,
    loading: authLoading || loading
  };
}